import { ContactList } from "components/ui/ContactList";
import { Wrench } from "lucide-react";
import { useWorkers } from "context/WorkersContext";
import { useEffect, useMemo } from "react";
import { useAuth } from "context/AuthContext";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import { workerListSubtitle } from "utils/contactListSubtitle";
import { ReplyMuteBadge } from "components/ReplyMuteBadge";
import type { ReplyMute } from "types/ReplyMute";

interface WorkersListProps {
  onSelectContact: (contactId: number) => void;
  selectedContactId: number | null;
  condoId: number;
}

type WorkerRow = {
  id: number;
  contactId: number;
  name: string;
  subtitle: string;
  lastMessageAt?: string | null;
  hasUnread?: boolean;
  isActive: boolean;
  rowKind: "condo" | "other";
  replyMute?: ReplyMute | null;
};

export function WorkersList({
  onSelectContact,
  selectedContactId,
  condoId,
}: WorkersListProps) {
  const {
    workers,
    otherCondoWorkers,
    loading,
    error,
    refresh,
    markContactAsRead,
  } = useWorkers();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const handleSelectContact = (contactId: number) => {
    markContactAsRead(contactId);
    onSelectContact(contactId);
  };

  useEffect(() => {
    refresh(condoId);
  }, [condoId, refresh]);

  const listItems: WorkerRow[] = useMemo(() => {
    const condoRows = workers.map((worker) => ({
      id: worker.id,
      contactId: worker.contact.id,
      name: worker.contact.name,
      subtitle: workerListSubtitle(worker, locale),
      lastMessageAt: worker.lastMessageAt,
      hasUnread: worker.hasUnread,
      isActive: worker.isActive ?? true,
      rowKind: "condo" as const,
      replyMute: worker.replyMute ?? null,
    }));
    const otherRows = otherCondoWorkers.map((worker) => ({
      id: worker.id,
      contactId: worker.contact.id,
      name: worker.contact.name,
      subtitle: workerListSubtitle(worker, locale),
      lastMessageAt: worker.lastMessageAt,
      hasUnread: worker.hasUnread,
      isActive: worker.isActive ?? true,
      rowKind: "other" as const,
      replyMute: worker.replyMute ?? null,
    }));
    return [...condoRows, ...otherRows];
  }, [workers, otherCondoWorkers, locale]);

  const otherBuildingsLabel =
    locale === "es" ? "Otros edificios" : "Other buildings";

  return (
    <ContactList<WorkerRow>
      listId="workers"
      title={locale === "es" ? "Trabajadores" : "Workers"}
      icon={<Wrench className="w-4 h-4 mr-2" />}
      items={listItems}
      loading={loading}
      error={error}
      searchPlaceholder={
        locale === "es" ? "Buscar trabajadores..." : "Search workers..."
      }
      selectedId={selectedContactId}
      onSelectItem={handleSelectContact}
      getItemName={(item) => `${item.name} ${item.subtitle}`.trim()}
      getSelectionId={(item) => item.contactId}
      countItemsForHeader={(item) => item.rowKind === "condo" && item.isActive}
      renderItem={(item, isSelected, { filteredItems, filteredIndex }) => {
        const muted = item.rowKind === "other";
        const isFirstOtherInFiltered =
          item.rowKind === "other" &&
          !filteredItems
            .slice(0, filteredIndex)
            .some((row) => row.rowKind === "other");
        return (
          <div>
            {isFirstOtherInFiltered && (
              <div className="px-2 pb-1 pt-2 text-xs font-medium text-gray-500">
                {otherBuildingsLabel}
              </div>
            )}
            <div
              className={contactListItemClasses(isSelected, {
                muted,
                inactive: item.rowKind === "condo" && !item.isActive,
              })}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div
                    className={`truncate text-xs lg:text-sm ${
                      item.hasUnread ? "font-bold" : "font-medium"
                    } ${muted || !item.isActive ? "text-gray-500" : ""}`}
                  >
                    {item.name}
                  </div>
                  <div className="text-xs text-gray-500">{item.subtitle}</div>
                  <ReplyMuteBadge mute={item.replyMute} locale={locale} />
                </div>
                {item.lastMessageAt && (
                  <div className="flex-shrink-0 text-xs text-gray-400">
                    {formatTimestamp(item.lastMessageAt, locale)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
